/**
 * CDN Helper Utility
 * 
 * Converts Appwrite storage URLs to CDN URLs for better performance
 */

/**
 * Convert Appwrite storage URL to CDN URL
 * @param {string} appwriteUrl - Original Appwrite storage URL
 * @returns {string} - CDN-optimized URL
 */
export function convertToCDN(appwriteUrl) {
  if (!appwriteUrl) return appwriteUrl;
  
  // Match Appwrite storage URL pattern (with or without query parameters)
  const regex = /https:\/\/sgp\.cloud\.appwrite\.io\/v1\/storage\/buckets\/([^\/]+)\/files\/([^\/\?]+)/;
  const match = appwriteUrl.match(regex);
  
  if (match) {
    const bucket = match[1];
    const fileId = match[2];
    const cdnUrl = `https://parvej.me/cdn/${bucket}/${fileId}`;
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 CDN Convert:', { original: appwriteUrl, cdn: cdnUrl });
    }
    
    return cdnUrl;
  }
  
  // Only warn in development
  if (process.env.NODE_ENV === 'development') {
    console.warn('⚠️ CDN Convert FAILED:', appwriteUrl);
  }
  
  // Return original URL if no match (for backwards compatibility)
  return appwriteUrl;
}

/**
 * Get optimized image URL with optional transformations
 * @param {string} url - Image URL
 * @param {object} options - Transformation options
 * @returns {string} - Optimized URL
 */
export function getOptimizedImageUrl(url, options = {}) {
  const cdnUrl = convertToCDN(url);
  
  // Future: Add query parameters for image transformations if needed
  // For now, just return CDN URL
  return cdnUrl;
}

/**
 * Preload critical images for better LCP
 * @param {string[]} urls - Array of image URLs to preload
 */
export function preloadImages(urls) {
  if (typeof document === 'undefined') return;
  
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = convertToCDN(url);
    document.head.appendChild(link);
  });
}

/**
 * Check if URL is already a CDN URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export function isCDNUrl(url) {
  return url && url.includes('parvej.me/cdn/');
}

/**
 * Batch convert multiple URLs
 * @param {string[]} urls - Array of Appwrite URLs
 * @returns {string[]} - Array of CDN URLs
 */
export function batchConvertToCDN(urls) {
  return urls.map(url => convertToCDN(url));
}
